import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Tune as DefaultsIcon,
  ToggleOn as StatusIcon,
  Settings as PrefsIcon,
  ManageAccounts as UsersIcon,
  ChevronRight as ChevronIcon,
} from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { useSecurity } from '../Contexts/SecurityProvider';

interface SettingsCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  chip: { label: string; color: string; bg: string };
  action: { label: string; disabled: boolean; path?: string };
  permission?: string;
}

const SETTINGS_CARDS: SettingsCard[] = [
  {
    title: 'Users & Roles',
    description: 'Manage EEAS user access, role assignments, and location scope.',
    icon: <UsersIcon sx={{ fontSize: 28 }} />,
    chip: { label: 'Primary', color: appColors.primary.main, bg: appColors.primary.light },
    action: { label: 'Open', disabled: false, path: '/settings/users' },
    permission: 'UserManagement.View',
  },
  {
    title: 'Locations & Assembly Areas',
    description: 'Manage sites, buildings, and predefined assembly areas used when creating incidents.',
    icon: <LocationIcon sx={{ fontSize: 28 }} />,
    chip: { label: 'Primary', color: appColors.primary.main, bg: appColors.primary.light },
    action: { label: 'Open', disabled: false, path: '/settings/locations' },
  },
  {
    title: 'Incident Defaults',
    description: 'Configure default incident notes, instructions, and location preferences.',
    icon: <DefaultsIcon sx={{ fontSize: 28 }} />,
    chip: { label: 'Planned', color: appColors.status.warning, bg: appColors.status.warningBg },
    action: { label: 'Coming Soon', disabled: true },
  },
  {
    title: 'Status Configuration',
    description: 'Review standard accountability statuses such as Safe, Injured, Missing, and Unknown.',
    icon: <StatusIcon sx={{ fontSize: 28 }} />,
    chip: { label: 'Planned', color: appColors.status.warning, bg: appColors.status.warningBg },
    action: { label: 'Coming Soon', disabled: true },
  },
  {
    title: 'System Preferences',
    description: 'Review application-level preferences such as refresh intervals and display behavior.',
    icon: <PrefsIcon sx={{ fontSize: 28 }} />,
    chip: { label: 'Planned', color: appColors.status.warning, bg: appColors.status.warningBg },
    action: { label: 'Coming Soon', disabled: true },
  },
];

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useSecurity();
  const visibleCards = SETTINGS_CARDS.filter(
    (card) => !card.permission || hasPermission(card.permission),
  );

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Settings
        </Typography>
        <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5 }}>
          Configure EEAS-owned operational data used before incidents are created.
        </Typography>
      </Box>

      {/* ── Settings cards grid ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        {visibleCards.map((card) => {
          const isPrimary = card.chip.label === 'Primary';
          return (
            <Box
              key={card.title}
              onClick={() => card.action.path && navigate(card.action.path)}
              sx={{
                bgcolor: appColors.surface.card,
                border: `1px solid ${isPrimary ? appColors.primary.main : appColors.border.default}`,
                borderRadius: '12px',
                px: 2.5,
                py: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                cursor: card.action.disabled ? 'default' : 'pointer',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                ...(!card.action.disabled && {
                  '&:hover': {
                    borderColor: appColors.primary.main,
                    boxShadow: appColors.shadow.cardHover,
                  },
                }),
              }}
            >
              {/* Top: icon + chip */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: isPrimary ? appColors.primary.light : appColors.surface.inactive,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isPrimary ? appColors.primary.main : appColors.text.caption,
                  }}
                >
                  {card.icon}
                </Box>
                <Chip
                  label={card.chip.label}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    borderRadius: '10px',
                    bgcolor: card.chip.bg,
                    color: card.chip.color,
                  }}
                />
              </Box>

              {/* Title + description */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: appColors.text.body, lineHeight: 1.5 }}>
                  {card.description}
                </Typography>
              </Box>

              {/* CTA */}
              <Box sx={{ mt: 'auto', pt: 0.5 }}>
                <Button
                  variant={isPrimary ? 'contained' : 'outlined'}
                  size="small"
                  endIcon={!card.action.disabled && <ChevronIcon />}
                  disabled={card.action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (card.action.path) navigate(card.action.path);
                  }}
                  sx={{ minWidth: 120 }}
                >
                  {card.action.label}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SettingsPage;
